'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { getDisplayableAlerts } from '@alltrails/alerts/utils/getDisplayableAlerts';
import useUser from '@alltrails/context/hooks/useUser';
import useIntersectionObserverCallback from '@alltrails/core/hooks/useIntersectionObserverCallback';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExposureEvent } from '@alltrails/experiments';
import type { Group } from '@alltrails/groups';
import useLogDepthScrolled from '@alltrails/layout/hooks/useLogDepthScrolled';
import { useCaptureSessionAttribution } from '@alltrails/session-storage';
import { useRecentlyViewedTrails } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import ReviewSummaryContext from '@/contexts/ReviewSummaryContext';
import useReviewSummary from '@/hooks/useReviewSummary';
import useSprigEvent from '@/hooks/useSprigEvent';
import styles from './TrailLayout.module.scss';

const AdminToolbar = dynamic(() => import('@/components/AdminToolbar/AdminToolbarWrapper'), { ssr: false });
const AdminEditor = dynamic(() => import('@/components/AdminEditor'), { ssr: false });
const PLPToolbar = dynamic(() => import('@/components/PLPToolbar/PLPToolbarWrapper'), { ssr: false });
const AdminNote = dynamic(() => import('@/components/AdminNote/AdminNoteWrapper'), { ssr: false });

const MAX_MOBILE_LCP_HEIGHT = '294px';
const threshold = Array.from({ length: 101 }, (_, i) => i / 100);

type TrailLayoutProps = {
  alerts: ReactNode;
  breadcrumbs: ReactNode;
  completionBadge: ReactNode;
  desktopCtas: ReactNode;
  desktopLcp: ReactNode;
  desktopNav: ReactNode;
  difficulty: ReactNode;
  managedBadge?: ReactNode;
  heading: ReactNode;
  isAdmin?: boolean;
  isBot?: boolean;
  isPLPUser?: boolean;
  mobileCtas: ReactNode;
  mobileLcp: ReactNode;
  mobileStickyBar: ReactNode;
  pageReferer: string | null;
  reviewSummary?: string;
  stats: ReactNode;
  trailDetails: TrailDetails;
  userGroups?: Group[];
};

const TrailLayout = ({
  alerts,
  breadcrumbs,
  children,
  completionBadge,
  desktopCtas,
  desktopLcp,
  desktopNav,
  difficulty,
  managedBadge,
  heading,
  isAdmin,
  isPLPUser,
  mobileCtas,
  mobileLcp,
  mobileStickyBar,
  pageReferer,
  reviewSummary,
  stats,
  trailDetails,
  userGroups
}: PropsWithChildren<TrailLayoutProps>) => {
  const {
    hideReviewSummary,
    isLoading: isHideReviewSummaryLoading,
    onToggleHideReviewSummary
  } = useReviewSummary({
    trailId: trailDetails.id,
    initialHideSummary: trailDetails.hideReviewSummary,
    initialReviewSummary: reviewSummary
  });

  const adminToolbarInfo = {
    id: trailDetails.id,
    slug: trailDetails.slug,
    area: trailDetails.area,
    name: trailDetails.name,
    latitude: trailDetails.location?.latitude,
    longitude: trailDetails?.location?.longitude,
    hideReviewSummary: trailDetails.hideReviewSummary
  };

  const desktopLcpRef = useRef<HTMLDivElement>(null);
  const imageScrollRef = useRef<HTMLDivElement>(null);
  const mobileContentRef = useRef<HTMLDivElement>(null);
  const mobileLcpRef = useRef<HTMLDivElement>(null);
  const adminToolbarRef = useRef<HTMLDivElement>(null);
  const plpToolbarRef = useRef<HTMLDivElement>(null);
  const [showMobileStickyBar, setShowMobileStickyBar] = useState(false);
  const [showAdminStickyBar, setShowAdminStickyBar] = useState(false);
  const isMobile = useIsMobileSizedScreen();

  useSprigEvent();
  useLogDepthScrolled();
  useCaptureSessionAttribution(pageReferer);

  const sendCellConnectivityExposure = useExposureEvent('web-growth-rm-cell-connectivity');

  const user = useUser();
  const { addRecentlyViewedTrail } = useRecentlyViewedTrails();

  useEffect(() => {
    if (user) {
      addRecentlyViewedTrail(trailDetails.id);
    }
    // Only want to add the trail once when the user is logged in
  }, [user]);

  const mobileContentObserver = useIntersectionObserverCallback(
    ([entry]) => {
      setShowMobileStickyBar(entry.boundingClientRect.top < 0);
    },
    { threshold }
  );

  useEffect(() => {
    const mobileContentSection = mobileContentRef.current;

    if (mobileContentObserver && mobileContentSection) {
      mobileContentObserver.observe(mobileContentSection);
    }

    return () => {
      if (mobileContentObserver) mobileContentObserver.disconnect();
    };
  }, [isMobile, mobileContentObserver]);

  useEffect(() => {
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
        const atTopOfPage = currentScrollTop === 0;
        if (mobileLcpRef.current) {
          // Zooming effect on image while scrolling up
          const scrollDepth = imageScrollRef.current?.getBoundingClientRect().top ?? 0;
          mobileLcpRef.current.style.height = `calc(${MAX_MOBILE_LCP_HEIGHT} + ${scrollDepth < 1 ? scrollDepth * -0.2 : 0}px`;
        }
        const shouldShowAdminStickyBar = !atTopOfPage;
        if (adminToolbarRef.current || plpToolbarRef.current) {
          setShowAdminStickyBar(shouldShowAdminStickyBar);
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (isMobile && trailDetails.lowCellService) {
      sendCellConnectivityExposure();
    }
  }, [isMobile, sendCellConnectivityExposure, trailDetails.lowCellService]);

  const reviewSummaryContextValue = {
    isLoading: isHideReviewSummaryLoading,
    hideReviewSummary,
    reviewSummary,
    onToggleHideReviewSummary
  };

  const content = (
    <ReviewSummaryContext.Provider value={reviewSummaryContextValue}>
      <div className={styles.container}>
        {isAdmin && (
          <div className={classNames(styles.adminToolbar, { [styles.showAdminStickyBar]: showAdminStickyBar })} ref={adminToolbarRef}>
            <AdminToolbar
              alerts={getDisplayableAlerts(trailDetails.alerts ?? [])}
              trailInfo={adminToolbarInfo}
              hideReviewSummary={hideReviewSummary ?? false}
              isHideReviewSummaryLoading={isHideReviewSummaryLoading}
              onToggleHideReviewSummary={onToggleHideReviewSummary}
            />
          </div>
        )}
        {isPLPUser && !isAdmin && (
          <div className={classNames(styles.adminToolbar, { [styles.showAdminStickyBar]: showAdminStickyBar })} ref={plpToolbarRef}>
            <PLPToolbar trailInfo={adminToolbarInfo} userGroups={userGroups} />
          </div>
        )}
        {isAdmin && <AdminNote trailDetails={trailDetails} />}
        <div
          className={classNames(styles.stickyBar, {
            [styles.showStickyBar]: showMobileStickyBar && isMobile,
            [styles.hasAdminToolbar]: isAdmin || isPLPUser
          })}
        >
          {mobileStickyBar}
        </div>
        {breadcrumbs}
      </div>
      <div className={styles.mobileLcpContainer}>
        {isMobile && (
          <div className={styles.mobileLcp} ref={mobileLcpRef} style={{ maxHeight: `calc(${MAX_MOBILE_LCP_HEIGHT} * 1.2)` }}>
            {mobileLcp}
          </div>
        )}
        <div className={styles.mobileCtas}>{mobileCtas}</div>
      </div>
      <div className={styles.mobileTopOfPage}>
        <div className={styles.container}>
          <div className={styles.topOfPage} ref={mobileContentRef}>
            {/* pseudo element used to track scroll position of photo element if it were not sticky */}
            <div ref={imageScrollRef} style={{ width: '1px', height: MAX_MOBILE_LCP_HEIGHT, position: 'absolute', bottom: '100%' }} />
            <div className={styles.completion}>{completionBadge}</div>
            {heading}
            {isMobile && (
              <div className={styles.mobileStats}>
                {!!managedBadge ? (
                  <div className={styles.mobileStatsRow}>
                    {difficulty}
                    {managedBadge}
                  </div>
                ) : (
                  difficulty
                )}
                <div className={styles.statsDivider} />
                <div className={styles.alerts}>{alerts}</div>
                {stats}
              </div>
            )}
            {!isMobile && (
              <div className={classNames(styles.alerts, { [styles.adminContainer]: isAdmin || isPLPUser })}>
                {alerts}
                {difficulty}
                {managedBadge}
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <>
            {desktopNav}
            {desktopCtas}
            <div className={styles.container}>
              <div className={styles.desktopLcp} ref={desktopLcpRef}>
                {desktopLcp}
              </div>
            </div>
          </>
        )}
        <div className={styles.content}>{children}</div>
      </div>
      {(isAdmin || isPLPUser) && <AdminEditor trailDetails={trailDetails} isOnlyPlpUser={isPLPUser && !isAdmin} />}
    </ReviewSummaryContext.Provider>
  );

  return content;
};

export default TrailLayout;
